import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Modal,
  StatTile,
  StatusPill,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Toggle,
  Tr
} from '../../components/admin/ui';
import { useConfirm, useToast } from '../../components/admin/overlays';
import { IconCases, IconClock, IconCourier, IconPlus, IconRefresh } from '../../components/admin/icons';
import { formatNumber } from '../../components/admin/labels';
import { fieldErrorsFrom } from '../../utils/formErrors';

const emptyForm = { name: '', isActive: true };

const CourierIntelligence = () => {
  const toast = useToast();
  const confirm = useConfirm();

  const [couriers, setCouriers] = useState([]);
  const [loadingCouriers, setLoadingCouriers] = useState(true);
  const [stats, setStats] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchCouriers = useCallback(async () => {
    setLoadingCouriers(true);
    try {
      const { data } = await api.get('/api/admin/couriers');
      setCouriers(data || []);
    } catch (error) {
      toast('Could not load couriers.', { tone: 'error' });
    } finally {
      setLoadingCouriers(false);
    }
  }, [toast]);

  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const response = await api.get('/api/admin/courier-intelligence');
      setStats(response.data || []);
    } catch (error) {
      toast('Could not load courier intelligence.', { tone: 'error' });
    } finally {
      setLoadingStats(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchCouriers();
    fetchStats();
  }, [fetchCouriers, fetchStats]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormErrors({});
    setShowForm(true);
  };

  const openEdit = (courier) => {
    setEditing(courier);
    setForm({ name: courier.name, isActive: courier.isActive });
    setFormErrors({});
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setFormErrors({ name: 'Enter a courier name.' });
      return;
    }
    setFormErrors({});
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/api/admin/couriers/${editing._id}`, {
          name: form.name.trim(),
          isActive: form.isActive
        });
        toast(`${form.name.trim()} updated.`, { tone: 'success' });
      } else {
        await api.post('/api/admin/couriers', {
          name: form.name.trim(),
          isActive: form.isActive
        });
        toast(`${form.name.trim()} added.`, { tone: 'success' });
      }
      setShowForm(false);
      fetchCouriers();
    } catch (error) {
      setFormErrors(fieldErrorsFrom(error, { fallback: 'Could not save this courier.' }));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (courier) => {
    try {
      await api.patch(`/api/admin/couriers/${courier._id}`, { isActive: !courier.isActive });
      fetchCouriers();
      toast(
        courier.isActive ? `${courier.name} deactivated.` : `${courier.name} activated.`,
        { tone: 'success' }
      );
    } catch (error) {
      toast('Could not update this courier.', { tone: 'error' });
    }
  };

  const handleDelete = async (courier) => {
    const ok = await confirm({
      title: 'Delete courier',
      message: `Delete ${courier.name}? This can't be undone.`,
      tone: 'danger',
      confirmLabel: 'Delete'
    });
    if (!ok) return;

    try {
      await api.delete(`/api/admin/couriers/${courier._id}`);
      toast(`${courier.name} deleted.`, { tone: 'success' });
      fetchCouriers();
    } catch (error) {
      toast(error.response?.data?.error || 'Could not delete this courier.', { tone: 'error' });
    }
  };

  const totalCases = stats.reduce((sum, stat) => sum + (stat.totalCases || 0), 0);
  const maxCases = stats.reduce((max, stat) => Math.max(max, stat.totalCases || 0), 0);
  const withResponse = stats.filter((stat) => stat.avgResponseTimeHours);
  const fastest = withResponse.length
    ? withResponse.reduce((best, stat) =>
        stat.avgResponseTimeHours < best.avgResponseTimeHours ? stat : best
      )
    : null;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Couriers"
          description="Couriers offered on the report form and every case filter."
          actions={
            <>
              <Button variant="ghost" size="sm" onClick={fetchCouriers}>
                <IconRefresh size={15} />
                Refresh
              </Button>
              <Button size="sm" onClick={openCreate}>
                <IconPlus size={15} />
                Add courier
              </Button>
            </>
          }
        />

        {loadingCouriers ? (
          <TableSkeleton rows={4} cols={4} />
        ) : couriers.length === 0 ? (
          <EmptyState
            icon={IconCourier}
            title="No couriers yet"
            description="Add a courier so it appears on the report form and case filters."
            action={
              <Button size="sm" onClick={openCreate}>
                <IconPlus size={15} />
                Add courier
              </Button>
            }
          />
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Status</Th>
                <Th>Cases</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {couriers.map((courier) => (
                <Tr key={courier._id}>
                  <Td className="font-600 text-ink-900">{courier.name}</Td>
                  <Td>
                    <StatusPill tone={courier.isActive ? 'success' : 'neutral'}>
                      {courier.isActive ? 'Active' : 'Inactive'}
                    </StatusPill>
                  </Td>
                  <Td className="tabular-nums">{formatNumber(courier.caseCount || 0)}</Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <Button variant="secondary" size="sm" onClick={() => openEdit(courier)}>
                        Edit
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => handleToggleActive(courier)}>
                        {courier.isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button variant="dangerGhost" size="sm" onClick={() => handleDelete(courier)}>
                        Delete
                      </Button>
                    </div>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>

      {!loadingStats && stats.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatTile
            label="Couriers seen"
            value={formatNumber(stats.length)}
            hint="Couriers with at least one case"
            icon={IconCourier}
          />
          <StatTile
            label="Cases covered"
            value={formatNumber(totalCases)}
            hint="Across all couriers"
            icon={IconCases}
          />
          <StatTile
            label="Fastest responder"
            value={fastest ? fastest.courier || 'Unknown' : '—'}
            hint={
              fastest
                ? `${Math.round(fastest.avgResponseTimeHours)} hours average response`
                : 'No response times recorded yet'
            }
            icon={IconClock}
            tone={fastest ? 'success' : 'neutral'}
          />
        </div>
      )}

      <Card>
        <CardHeader
          title="Courier performance"
          description="Case volume and average merchant response time, by courier."
          actions={
            <Button variant="ghost" size="sm" onClick={fetchStats}>
              <IconRefresh size={15} />
              Refresh
            </Button>
          }
        />

        {loadingStats ? (
          <TableSkeleton rows={4} cols={3} />
        ) : stats.length === 0 ? (
          <EmptyState
            icon={IconCourier}
            title="No courier statistics yet"
            description="Once cases are submitted and merchants respond, per-courier volumes and response times appear here."
          />
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th>Courier</Th>
                <Th>Cases</Th>
                <Th className="w-[38%]">Share of total</Th>
                <Th>Avg response time</Th>
              </tr>
            </thead>
            <tbody>
              {[...stats]
                .sort((a, b) => (b.totalCases || 0) - (a.totalCases || 0))
                .map((stat, index) => (
                  <Tr key={`${stat.courier || 'unknown'}-${index}`}>
                    <Td className="font-600 text-ink-900">{stat.courier || 'Unknown'}</Td>
                    <Td className="tabular-nums">{formatNumber(stat.totalCases || 0)}</Td>
                    <Td>
                      <div className="flex items-center gap-3">
                        <div className="h-2 w-full max-w-[12rem] overflow-hidden rounded-full bg-ink-100">
                          <div
                            className="h-full origin-left animate-grow rounded-[4px] bg-brand-600"
                            style={{
                              width: `${
                                maxCases ? Math.max(((stat.totalCases || 0) / maxCases) * 100, 2) : 0
                              }%`
                            }}
                          />
                        </div>
                        <span className="shrink-0 text-xs tabular-nums text-ink-500">
                          {totalCases ? Math.round(((stat.totalCases || 0) / totalCases) * 100) : 0}%
                        </span>
                      </div>
                    </Td>
                    <Td className="tabular-nums text-ink-600">
                      {stat.avgResponseTimeHours
                        ? `${Math.round(stat.avgResponseTimeHours)} hours`
                        : <span className="text-ink-500">Not recorded</span>}
                    </Td>
                  </Tr>
                ))}
            </tbody>
          </TableWrap>
        )}
      </Card>

      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? 'Edit courier' : 'Add courier'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saving}>
              {editing ? 'Save changes' : 'Add courier'}
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          <Field label="Name" htmlFor="courier-name" required error={formErrors.name}>
            <Input
              id="courier-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Yodel"
              autoFocus
            />
          </Field>

          <div className="rounded-xl border border-ink-200 p-4">
            <Toggle
              id="courier-active"
              label="Active"
              description="Only active couriers appear on the report form and case filters."
              checked={form.isActive}
              onChange={(checked) => setForm({ ...form, isActive: checked })}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CourierIntelligence;
